/**
 * DocumentRequirementsPage — Documents & Disclosures · Document Requirements.
 *
 * Full-CRUD editor for the DocumentRequirements catalog — the per-entity-type
 * upload-slot matrix that drives the People-Form upload grid and the Disclosure
 * Engine's packet assembly. Officers see every change on their next page load,
 * no deploy required.
 *
 * Contract (Wave 6.1 write-routes-compliance):
 *   - Read:  client.retail.config.documentRequirements.list() (typed; seeded
 *            rows across the entity-type catalog)
 *   - Write: POST /v3/admin/document-requirements
 *            (natural key EntityType+DocCode for new rows; RequirementId for
 *             updates; soft-delete = re-upsert IsActive=false)
 *
 * Migrated 2026-06-16 from ROP-teams Admin (operational app writes no config).
 * Auth: ADMIN_AUTH_TOKEN_SENTINEL → buildAdminAuthHeaders omits the bypass-key
 * (MSAL bearer carries authority; no secret in the bundle).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ADMIN_AUTH_TOKEN_SENTINEL,
  ApiError,
  buildAdminAuthHeaders,
} from '@retail-ops/api-client';
import type {
  DocumentRequirement,
  RequirementLevel,
  ImagingBarcodeFormat,
  ImagingBarcodePosition,
  ImagingBarcodeSize,
} from '@retail-ops/shared-types';
import { useApiClient } from '@/contexts/ApiClientContext';
import { Banner, Button, Card, Pill, Spinner } from '@/components/primitives';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { TextField, SelectField, NumberField, BoolField, TextArea } from '@/components/FormField';
import { ModalShell } from '@/components/ModalShell';

type EditableRow = DocumentRequirement;

interface AdminWriteEnvelope {
  success: boolean;
  data?: { requirementId?: number; entityType?: string; docCode?: string; created?: boolean };
  error?: string;
  code?: string;
}

const REQUIREMENT_LEVELS: ReadonlyArray<RequirementLevel> = ['Hard', 'Soft', 'Recommended'];
const BARCODE_FORMATS: ReadonlyArray<ImagingBarcodeFormat> = ['Code128', 'Code39', 'QR', 'DataMatrix', 'PDF417'];
const BARCODE_POSITIONS: ReadonlyArray<ImagingBarcodePosition> = ['TopRight', 'TopLeft', 'Footer', 'Coversheet', 'EveryPage'];
const BARCODE_SIZES: ReadonlyArray<ImagingBarcodeSize> = ['Small', 'Medium', 'Large'];

const EMPTY_ROW: EditableRow = {
  requirementId: 0,
  entityType: '',
  docCode: '',
  docLabel: '',
  requirementLevel: 'Hard',
  softDueOffsetDays: null,
  taskTemplateId: null,
  sortOrder: 1,
  isActive: true,
  notes: null,
  imagingBarcodeFormat: null,
  imagingBarcodeTemplate: null,
  imagingBarcodePosition: null,
  imagingBarcodeSize: null,
  imagingSystemDocType: null,
  imagingIndexFieldsJson: null,
  imagingMeta: null,
};

const LEVEL_OPTIONS = REQUIREMENT_LEVELS.map((l) => ({ value: l, label: l }));

// Nullable-select options ('' = unset → stored null).
function optionalOptions<T extends string>(values: ReadonlyArray<T>, unsetLabel: string) {
  return [{ value: '', label: unsetLabel }, ...values.map((v) => ({ value: v, label: v }))];
}

function levelTone(level: RequirementLevel): 'err' | 'warn' | 'muted' {
  return level === 'Hard' ? 'err' : level === 'Soft' ? 'warn' : 'muted';
}

function sortRows(rows: ReadonlyArray<DocumentRequirement>): DocumentRequirement[] {
  return rows.slice().sort((a, b) => {
    if (a.entityType !== b.entityType) return a.entityType.localeCompare(b.entityType);
    if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
    return a.docCode.localeCompare(b.docCode);
  });
}

export function DocumentRequirementsPage(): JSX.Element {
  const client = useApiClient();
  const [rows, setRows] = useState<ReadonlyArray<DocumentRequirement> | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [entityFilter, setEntityFilter] = useState('');
  const [labelSearch, setLabelSearch] = useState('');
  const [editRow, setEditRow] = useState<EditableRow | null>(null);
  const [isNewRow, setIsNewRow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DocumentRequirement | null>(null);
  const [busyRowId, setBusyRowId] = useState<number | null>(null);
  const [toast, setToast] = useState<{ tone: 'info' | 'warn'; text: string } | null>(null);
  const [refreshNonce, setRefreshNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setRows(null);
      setLoadError(null);
      try {
        // Admin editor must see inactive rows (so a deactivate is visible +
        // reversible). The typed config.documentRequirements.list() hits the
        // officer-facing read which is hard-filtered to active-only with no
        // includeInactive option; read raw with includeInactive=true instead
        // (backend support added 2026-06-17, BACKLOG-0579 verify fix). The two
        // officer callers keep the active-only typed method untouched.
        const resp = await client.http.get<{
          success: boolean;
          data?: { requirements: ReadonlyArray<DocumentRequirement> };
          error?: { message?: string } | string;
        }>('/v3/config/document-requirements?includeInactive=true');
        if (cancelled) return;
        if (resp.success && resp.data) {
          setRows(sortRows(resp.data.requirements));
        } else {
          setLoadError(
            (resp as { error?: { message?: string } | string }).error
              ? typeof (resp as { error?: { message?: string } | string }).error === 'string'
                ? ((resp as { error?: string }).error as string)
                : ((resp as { error?: { message?: string } }).error?.message ?? 'Failed to load.')
              : 'Failed to load DocumentRequirements.',
          );
        }
      } catch (e) {
        if (cancelled) return;
        setLoadError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, refreshNonce]);

  const refresh = useCallback(() => setRefreshNonce((n) => n + 1), []);

  const distinctEntityTypes = useMemo<ReadonlyArray<string>>(() => {
    const set = new Set<string>();
    for (const r of rows ?? []) set.add(r.entityType);
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const visibleRows = useMemo<ReadonlyArray<DocumentRequirement>>(() => {
    const needle = labelSearch.trim().toLowerCase();
    return (rows ?? []).filter((r) => {
      if (entityFilter && r.entityType !== entityFilter) return false;
      if (needle && !r.docLabel.toLowerCase().includes(needle)) return false;
      return true;
    });
  }, [rows, entityFilter, labelSearch]);

  const saveRow = useCallback(
    async (row: EditableRow): Promise<boolean> => {
      const resp = await client.http.post<AdminWriteEnvelope>('/v3/admin/document-requirements', {
        headers: buildAdminAuthHeaders(undefined, ADMIN_AUTH_TOKEN_SENTINEL),
        body: {
          requirementId: row.requirementId && row.requirementId > 0 ? row.requirementId : null,
          entityType: row.entityType,
          docCode: row.docCode,
          docLabel: row.docLabel,
          requirementLevel: row.requirementLevel,
          softDueOffsetDays: row.softDueOffsetDays,
          taskTemplateId: row.taskTemplateId,
          sortOrder: row.sortOrder,
          isActive: row.isActive,
          notes: row.notes,
          imagingBarcodeFormat: row.imagingBarcodeFormat,
          imagingBarcodeTemplate: row.imagingBarcodeTemplate,
          imagingBarcodePosition: row.imagingBarcodePosition,
          imagingBarcodeSize: row.imagingBarcodeSize,
          imagingSystemDocType: row.imagingSystemDocType,
          imagingIndexFieldsJson: row.imagingIndexFieldsJson,
          imagingMeta: row.imagingMeta,
        },
      });
      if (!resp.success) throw new Error(resp.error ?? 'admin write returned success=false');
      return true;
    },
    [client],
  );

  const handleModalSave = useCallback(async () => {
    if (!editRow) return;
    if (!editRow.entityType.trim() || !editRow.docCode.trim() || !editRow.docLabel.trim()) {
      setSaveError('EntityType, DocCode, and DocLabel are all required.');
      return;
    }
    if (editRow.imagingIndexFieldsJson?.trim()) {
      try {
        JSON.parse(editRow.imagingIndexFieldsJson);
      } catch {
        setSaveError('Imaging index fields must be valid JSON (or blank).');
        return;
      }
    }
    setSaving(true);
    setSaveError(null);
    try {
      await saveRow(editRow);
      setEditRow(null);
      setToast({ tone: 'info', text: 'Saved — officers see this change on next page load.' });
      refresh();
    } catch (e) {
      setSaveError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }, [editRow, saveRow, refresh]);

  const handleToggleActive = useCallback(
    async (row: DocumentRequirement) => {
      setBusyRowId(row.requirementId);
      try {
        await saveRow({ ...row, isActive: !row.isActive });
        setToast({ tone: 'info', text: `${row.docLabel} is now ${!row.isActive ? 'active' : 'inactive'}.` });
        refresh();
      } catch (e) {
        setToast({ tone: 'warn', text: `Toggle failed: ${e instanceof Error ? e.message : String(e)}` });
      } finally {
        setBusyRowId(null);
      }
    },
    [saveRow, refresh],
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setSaving(true);
    setSaveError(null);
    try {
      // Soft-delete = re-upsert IsActive=false (audit + identity PK preserved).
      await saveRow({ ...deleteTarget, isActive: false });
      setToast({ tone: 'warn', text: `Soft-deleted ${deleteTarget.docLabel} (IsActive=false).` });
      setDeleteTarget(null);
      refresh();
    } catch (e) {
      setSaveError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }, [deleteTarget, saveRow, refresh]);

  const columns: ReadonlyArray<DataTableColumn<DocumentRequirement>> = useMemo(
    () => [
      { header: 'Entity', cell: (r) => <code className="cell-mono">{r.entityType}</code> },
      { header: 'Doc code', cell: (r) => <code className="cell-mono--bold">{r.docCode}</code> },
      { header: 'Label', cell: (r) => <span className="cell-desc">{r.docLabel}</span> },
      { header: 'Level', cell: (r) => <Pill tone={levelTone(r.requirementLevel)}>{r.requirementLevel}</Pill> },
      { header: 'Sort', cell: (r) => <span className="num">{r.sortOrder}</span> },
      {
        header: 'Active',
        cell: (r) =>
          r.isActive ? <Pill tone="ok">active</Pill> : <Pill tone="muted">inactive</Pill>,
      },
      {
        header: '',
        cell: (r) => (
          <div className="card-actions-row">
            <Button onClick={() => { setEditRow({ ...r }); setIsNewRow(false); setSaveError(null); }}>
              Edit
            </Button>
            <Button onClick={() => void handleToggleActive(r)} disabled={busyRowId === r.requirementId}>
              {r.isActive ? 'Deactivate' : 'Activate'}
            </Button>
            {r.isActive && <Button onClick={() => setDeleteTarget(r)}>Delete</Button>}
          </div>
        ),
      },
    ],
    [busyRowId, handleToggleActive],
  );

  if (loadError) {
    return (
      <Banner tone="err">
        Could not load Document Requirements: {loadError} <Button onClick={refresh}>Retry</Button>
      </Banner>
    );
  }
  if (!rows) return <Spinner label="Loading Document Requirements…" />;

  return (
    <>
      {toast && (
        <Banner tone={toast.tone}>
          {toast.text} <Button onClick={() => setToast(null)}>Dismiss</Button>
        </Banner>
      )}

      <Card
        title={`Document Requirements — ${visibleRows.length} of ${rows.length}`}
        actions={
          <div className="card-actions-row">
            <div className="users-filter">
              <SelectField
                label=""
                value={entityFilter}
                onChange={setEntityFilter}
                options={[
                  { value: '', label: `All entity types (${distinctEntityTypes.length})` },
                  ...distinctEntityTypes.map((et) => ({ value: et, label: et })),
                ]}
              />
            </div>
            <div className="users-filter">
              <TextField label="" value={labelSearch} onChange={setLabelSearch} placeholder="Filter by label…" />
            </div>
            <Button variant="primary" onClick={() => { setEditRow({ ...EMPTY_ROW }); setIsNewRow(true); setSaveError(null); }}>
              + Add Requirement
            </Button>
          </div>
        }
      >
        <DataTable<DocumentRequirement>
          columns={columns}
          rows={visibleRows}
          rowKey={(r) => String(r.requirementId)}
          emptyState={<p className="empty">No requirements match the current filter.</p>}
        />
      </Card>

      <ModalShell
        open={editRow !== null}
        title={isNewRow ? 'Add Requirement' : `Edit ${editRow?.docLabel ?? ''}`}
        onClose={() => {
          if (saving) return;
          setEditRow(null);
          setSaveError(null);
        }}
        width={720}
        actions={
          editRow && (
            <>
              <Button onClick={() => setEditRow(null)} disabled={saving}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => void handleModalSave()} disabled={saving}>
                {saving ? 'Saving…' : isNewRow ? 'Create' : 'Save changes'}
              </Button>
            </>
          )
        }
      >
        {editRow && (
          <div className="form-stack">
            {saveError && <Banner tone="err">{saveError}</Banner>}

            <div className="form-row">
              <TextField
                label="Entity type"
                value={editRow.entityType}
                onChange={(v) => setEditRow({ ...editRow, entityType: v })}
                placeholder="e.g. Individual, SoleProprietor, LLC"
                disabled={!isNewRow}
                monospace
                hint={isNewRow ? 'Part of the natural key (EntityType + DocCode).' : 'Key is fixed once created.'}
              />
              <TextField
                label="Doc code"
                value={editRow.docCode}
                onChange={(v) => setEditRow({ ...editRow, docCode: v })}
                placeholder="e.g. DL_FRONT, SSN_CARD, EIN_LETTER"
                disabled={!isNewRow}
                monospace
              />
            </div>

            <TextField
              label="Doc label"
              value={editRow.docLabel}
              onChange={(v) => setEditRow({ ...editRow, docLabel: v })}
              placeholder="Officer-facing label, e.g. Driver's License (front)"
            />

            <div className="form-row">
              <SelectField
                label="Requirement level"
                value={editRow.requirementLevel}
                onChange={(v) => setEditRow({ ...editRow, requirementLevel: v as RequirementLevel })}
                options={LEVEL_OPTIONS}
                hint="Hard = blocks; Soft = due-by offset; Recommended = nudge only."
              />
              <NumberField
                label="Soft due offset (days)"
                value={editRow.softDueOffsetDays ?? null}
                onChange={(v) => setEditRow({ ...editRow, softDueOffsetDays: v })}
                min={0}
                placeholder="(Soft only)"
              />
            </div>

            <div className="form-row">
              <NumberField
                label="Task template ID"
                value={editRow.taskTemplateId ?? null}
                onChange={(v) => setEditRow({ ...editRow, taskTemplateId: v })}
                min={1}
                placeholder="(optional)"
              />
              <NumberField
                label="Sort order"
                value={editRow.sortOrder}
                onChange={(v) => setEditRow({ ...editRow, sortOrder: v ?? 1 })}
                min={1}
              />
            </div>

            <TextField
              label="Notes"
              value={editRow.notes ?? ''}
              onChange={(v) => setEditRow({ ...editRow, notes: v || null })}
              placeholder="(optional)"
            />

            <BoolField
              label="Active"
              value={editRow.isActive}
              onChange={(v) => setEditRow({ ...editRow, isActive: v })}
              hint="Inactive rows stay for audit but don't appear in the officer upload grid."
            />

            <details className="collapsible">
              <summary className="collapsible__summary">
                CSPI Aurora imaging (forward-compat — leave blank until imaging launches)
              </summary>
              <div className="form-stack collapsible__body">
                <div className="form-row">
                  <SelectField
                    label="Barcode format"
                    value={editRow.imagingBarcodeFormat ?? ''}
                    onChange={(v) => setEditRow({ ...editRow, imagingBarcodeFormat: (v || null) as ImagingBarcodeFormat | null })}
                    options={optionalOptions(BARCODE_FORMATS, '(none)')}
                  />
                  <SelectField
                    label="Barcode position"
                    value={editRow.imagingBarcodePosition ?? ''}
                    onChange={(v) => setEditRow({ ...editRow, imagingBarcodePosition: (v || null) as ImagingBarcodePosition | null })}
                    options={optionalOptions(BARCODE_POSITIONS, '(none)')}
                  />
                </div>
                <div className="form-row">
                  <SelectField
                    label="Barcode size"
                    value={editRow.imagingBarcodeSize ?? ''}
                    onChange={(v) => setEditRow({ ...editRow, imagingBarcodeSize: (v || null) as ImagingBarcodeSize | null })}
                    options={optionalOptions(BARCODE_SIZES, '(none)')}
                  />
                  <TextField
                    label="System doc type"
                    value={editRow.imagingSystemDocType ?? ''}
                    onChange={(v) => setEditRow({ ...editRow, imagingSystemDocType: v || null })}
                    monospace
                  />
                </div>
                <TextField
                  label="Barcode template"
                  value={editRow.imagingBarcodeTemplate ?? ''}
                  onChange={(v) => setEditRow({ ...editRow, imagingBarcodeTemplate: v || null })}
                  monospace
                />
                <TextArea
                  label="Index fields (JSON)"
                  value={editRow.imagingIndexFieldsJson ?? ''}
                  onChange={(v) => setEditRow({ ...editRow, imagingIndexFieldsJson: v || null })}
                  rows={2}
                  placeholder='{"acctNumber":"...","cif":"..."}'
                />
                <TextArea
                  label="Imaging meta"
                  value={editRow.imagingMeta ?? ''}
                  onChange={(v) => setEditRow({ ...editRow, imagingMeta: v || null })}
                  rows={2}
                />
              </div>
            </details>
          </div>
        )}
      </ModalShell>

      <ModalShell
        open={deleteTarget !== null}
        title="Soft-delete this requirement?"
        onClose={() => {
          if (saving) return;
          setDeleteTarget(null);
          setSaveError(null);
        }}
        width={520}
        actions={
          deleteTarget && (
            <>
              <Button onClick={() => setDeleteTarget(null)} disabled={saving}>
                Keep active
              </Button>
              <Button variant="primary" onClick={() => void handleConfirmDelete()} disabled={saving}>
                {saving ? 'Working…' : 'Soft-delete'}
              </Button>
            </>
          )
        }
      >
        {deleteTarget && (
          <div className="form-stack">
            {saveError && <Banner tone="err">{saveError}</Banner>}
            <p className="modal-prose">
              We'll set <strong>{deleteTarget.docLabel}</strong> (
              <code className="cell-mono">{deleteTarget.entityType}/{deleteTarget.docCode}</code>) to
              IsActive=false. The row stays for audit and can be reactivated; it just drops out of the
              officer upload grid.
            </p>
          </div>
        )}
      </ModalShell>
    </>
  );
}
